import type { HeatmapTile, HeatmapTileData } from "~types/types";

export type DataItem<Custom> = {
  value: number;
  tileData: HeatmapTileData<Custom>;
  children?: Array<DataItem<Custom>>;
} & Custom;

export type NormalizedDataItem<Custom> = DataItem<Custom> & {
  // this is the value after going through `normalizeData` function
  normalizedValue: number;
};
export interface Rect {
  // Top-left corner:
  x0: number;
  y0: number;
  // Bottom right corner:
  x1: number;
  y1: number;
}
export type LayoutRect<T> = Rect & NormalizedDataItem<T>;

// Calculate the max aspect ratio if a list of rectangles whose areas are contained in `row`
// are laid out against a line of length `length`:
// This formula is based on the idea that the maximum aspect ratio can be achieved by either:
// Placing the data item with the maximum area at one end of the length, and packing the remaining data items as tightly as possible. This is represented by the first term in the formula: (length ** 2 * maxArea) / sumArea ** 2.
// Placing the data item with the minimum area at one end of the length, and packing the remaining data items as loosely as possible. This is represented by the second term in the formula: sumArea ** 2 / (length ** 2 * minArea).
export const calculateMaxAspectRatio = <Custom>(
  row: Array<NormalizedDataItem<Custom>>,
  length: number
): number => {
  const rowLength = row.length;

  if (rowLength === 0) {
    throw new Error(`Inpupt ${row} is an empty array`);
  } else {
    let minArea = Infinity;
    let maxArea = -Infinity;
    let sumArea = 0;

    for (let i = 0; i < rowLength; i += 1) {
      const area = row[i].normalizedValue;

      if (area < minArea) {
        minArea = area;
      }
      if (area > maxArea) {
        maxArea = area;
      }
      sumArea += area;
    }
    const result = Math.max(
      (length ** 2 * maxArea) / sumArea ** 2,
      sumArea ** 2 / (length ** 2 * minArea)
    );

    return result;
  }
};

export const doesAddingToRowImproveAspectRatio = <Custom>(
  currentRow: Array<NormalizedDataItem<Custom>>,
  nextDatum: NormalizedDataItem<Custom>,
  length: number
): boolean => {
  if (currentRow.length === 0) {
    return true;
  }
  const newRow = currentRow.concat(nextDatum);
  const currentMaxAspectRatio = calculateMaxAspectRatio(currentRow, length);
  const newMaxAspectRatio = calculateMaxAspectRatio(newRow, length);

  return currentMaxAspectRatio >= newMaxAspectRatio;
};

// Normalizing our input values
// Scaling the values for each data item so that the sum of elements in array of data
// is equal to area of the layout we are trying to fit (as per original algorithm):
export const normalizeData = <Custom>(
  data: Array<DataItem<Custom>>,
  area: number
): Array<NormalizedDataItem<Custom>> => {
  const dataLength = data.length;
  let dataSum = 0;

  for (let i = 0; i < dataLength; i += 1) {
    dataSum += data[i].value;
  }

  const multiplier = area / dataSum;

  const result: Array<NormalizedDataItem<Custom>> = [];
  let elementResult: NormalizedDataItem<Custom>;
  let datum: DataItem<Custom>;

  for (let j = 0; j < dataLength; j += 1) {
    datum = data[j];
    elementResult = Object.assign({}, datum, {
      normalizedValue: datum.value * multiplier,
    });
    result.push(elementResult);
  }

  return result;
};

interface IContainer {
  xOffset: number;
  yOffset: number;
  height: number;
  width: number;
}

const containerToRect = (container: IContainer): Rect => {
  const xOffset = container.xOffset;
  const yOffset = container.yOffset;
  const width = container.width;
  const height = container.height;

  return {
    x0: xOffset,
    y0: yOffset,
    x1: xOffset + width,
    y1: yOffset + height,
  };
};

const rectToContainer = (rect: Rect): IContainer => {
  const { x0: xOffset, y0: yOffset, x1, y1 } = rect;

  return {
    xOffset,
    yOffset,
    width: x1 - xOffset,
    height: y1 - yOffset,
  };
};

const getShortestEdge = (input: Rect) => {
  const { width, height } = rectToContainer(input);

  return Math.min(width, height);
};

// Lay the rectangles inside a `container` so that the rectangles stack on top of each other in the direction
// parallel to the width:
// It determines the width and height of each rectangle by dividing the normalized value by
// The areaWidth and areaHeight calculated from the available layout space
export const getCoordinates = <Custom>(
  row: Array<NormalizedDataItem<Custom>>,
  rect: Rect
): Array<LayoutRect<Custom>> => {
  const { width, height, xOffset, yOffset } = rectToContainer(rect);

  const rowLength = row.length;
  let valueSum = 0;

  for (let i = 0; i < rowLength; i += 1) {
    valueSum += row[i].normalizedValue;
  }

  const areaWidth = valueSum / height;
  const areaHeight = valueSum / width;

  let subXOffset = xOffset;
  let subYOffset = yOffset;
  const coordinates: Array<LayoutRect<Custom>> = [];

  if (width >= height) {
    for (let i = 0; i < rowLength; i += 1) {
      const num = row[i];
      const y1 = subYOffset + num.normalizedValue / areaWidth;
      const rectangle: Rect = {
        x0: subXOffset,
        y0: subYOffset,
        x1: subXOffset + areaWidth,
        y1,
      };
      const nextCoordinate: LayoutRect<Custom> = Object.assign(
        {},
        num,
        rectangle
      );

      subYOffset = y1;
      coordinates.push(nextCoordinate);
    }

    return coordinates;
  }
  for (let i = 0; i < rowLength; i += 1) {
    const num = row[i];
    const x1 = subXOffset + num.normalizedValue / areaHeight;
    const rectangle: Rect = {
      x0: subXOffset,
      y0: subYOffset,
      x1,
      y1: subYOffset + areaHeight,
    };
    const nextCoordinate: LayoutRect<Custom> = Object.assign(
      {},
      num,
      rectangle
    );

    subXOffset = x1;
    coordinates.push(nextCoordinate);
  }

  return coordinates;
};

// Finds the remaining area where rectangles have not been laid out
export const cutArea = (rect: Rect, area: number): Rect => {
  const { width, height, xOffset, yOffset } = rectToContainer(rect);

  if (width >= height) {
    const areaWidth = area / height;
    const newWidth = width - areaWidth;
    const container: IContainer = {
      xOffset: xOffset + areaWidth,
      yOffset,
      width: newWidth,
      height,
    };

    return containerToRect(container);
  }
  const areaHeight = area / width;
  const newHeight = height - areaHeight;
  const container: IContainer = {
    xOffset,
    yOffset: yOffset + areaHeight,
    width,
    height: newHeight,
  };

  return containerToRect(container);
};

// Repeatedly cuts a rectangle into smaller parts and lays out the data items within each part.
// This process continues until all data items have been processed, at which point the final stack of layout rectangles is returned.
//     1. Get the shortest edge of the rectangle
//     2. Check if adding the next data item to the current row improves the aspect ratio
//     3. If adding the next data item to the current row does not improve the aspect ratio, cut the rectangle
//     4. Stop when no more data to process
export const squarify = <Custom>(
  // Array of normalized data to be layed out
  inputData: Array<NormalizedDataItem<Custom>>,
  // Current row of data items
  inputCurrentRow: Array<NormalizedDataItem<Custom>>,
  // Rectangle where data items are to be laid out
  inputRect: Rect,
  // stack of rectangles laid out so far
  inputStack: Array<LayoutRect<Custom>>
): Array<LayoutRect<Custom>> => {
  let data: Array<NormalizedDataItem<Custom>> = inputData;
  let currentRow: Array<NormalizedDataItem<Custom>> = inputCurrentRow;
  let rect: Rect = inputRect;
  let stack: Array<LayoutRect<Custom>> = inputStack;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const dataLength = data.length;

    if (dataLength === 0) {
      const newCoordinates = getCoordinates(currentRow, rect);
      const newStack: Array<LayoutRect<Custom>> = stack.concat(newCoordinates);

      return newStack;
    }

    const width = getShortestEdge(rect);
    const nextDatum = data[0];
    const restData = data.slice(1, dataLength);

    if (doesAddingToRowImproveAspectRatio(currentRow, nextDatum, width)) {
      const newRow = currentRow.concat(nextDatum);

      data = restData;
      currentRow = newRow;
      // eslint-disable-next-line no-self-assign
      rect = rect;
      // eslint-disable-next-line no-self-assign
      stack = stack;
    } else {
      const currentRowLength = currentRow.length;
      let valueSum = 0;

      for (let i = 0; i < currentRowLength; i += 1) {
        valueSum += currentRow[i].normalizedValue;
      }

      const newContainer = cutArea(rect, valueSum);
      const newCoordinates = getCoordinates(currentRow, rect);
      const newStack = stack.concat(newCoordinates);

      // eslint-disable-next-line no-self-assign
      data = data;
      currentRow = [];
      rect = newContainer;
      stack = newStack;
    }
  }
};

const getArea = (rect: Rect) => (rect.x1 - rect.x0) * (rect.y1 - rect.y0);

// recursing into each level of the hierarchical data and applying the squarify function
export const recurse = <Custom>(
  originalLayoutAndData: LayoutRect<Custom>
): Array<LayoutRect<Custom>> => {
  if (originalLayoutAndData.children === undefined) {
    const result = [originalLayoutAndData];

    return result;
  }
  const normalizedChildren: Array<NormalizedDataItem<Custom>> =
    normalizeData<Custom>(
      originalLayoutAndData.children,
      getArea(originalLayoutAndData)
    );
  const squarified: Array<LayoutRect<Custom>> = squarify(
    normalizedChildren,
    [],
    originalLayoutAndData,
    []
  );

  const squarifiedLength = squarified.length;
  const contained: Array<Array<LayoutRect<Custom>>> = [];

  for (let i = 0; i < squarifiedLength; i += 1) {
    contained.push(recurse(squarified[i]));
  }

  const flattened: Array<LayoutRect<Custom>> = contained.flat();

  return flattened;
};

// Base function - takes an input of Heatmap tile data and starts the recursion
// to fill the rectangles into the area. Our case currently has no children in the
// input array. We input one flat array of tickers.
export default function squarifiedHeatmap<Custom>(
  data: Array<HeatmapTileData<Custom>>,
  container: { x0: number; y0: number; x1: number; y1: number }
): Array<HeatmapTile<Custom>> {
  const x0 = container.x0;
  const y0 = container.y0;
  const x1 = container.x1;
  const y1 = container.y1;

  const formattedData: Array<DataItem<Custom>> = data.map(
    (tileInput) =>
      ({
        value: tileInput.size,
        tileData: tileInput,
      } as DataItem<Custom>)
  );

  const input = {
    x0,
    y0,
    x1,
    y1,
    children: formattedData,
  } as LayoutRect<Custom>;

  const result: Array<LayoutRect<Custom>> = recurse(input);
  const formattedResult: Array<HeatmapTile<Custom>> = result.map(
    (squarifiedTile) => ({
      x: squarifiedTile.x0,
      y: squarifiedTile.y0,
      width: squarifiedTile.x1 - squarifiedTile.x0,
      height: squarifiedTile.y1 - squarifiedTile.y0,
      data: squarifiedTile.tileData,
    })
  );

  return formattedResult;
}
